import React, { useState, useRef, useEffect, useCallback } from 'react'
import { X, Upload, Image as ImageIcon, RefreshCw } from 'lucide-react'

interface ProjectUploadDialogProps {
  projectId: string
  projectName: string
  currentCoverImage?: string
  currentDescription?: string
  onClose: () => void
  onConfirm: (data: { name: string; coverImage: string | null | undefined; coverThumbnailFileId: string | null | undefined; description: string }) => Promise<void>
}

export const ProjectUploadDialog: React.FC<ProjectUploadDialogProps> = ({
  projectId,
  projectName,
  currentCoverImage,
  currentDescription,
  onClose,
  onConfirm
}) => {
  const [name, setName] = useState(projectName)
  const [description, setDescription] = useState(currentDescription || '')
  const [coverImageUrl, setCoverImageUrl] = useState<string | null>(null)
  const [coverFileId, setCoverFileId] = useState<string | null>(null)
  const [coverThumbnailFileId, setCoverThumbnailFileId] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isGeneratingCover, setIsGeneratingCover] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const coverAreaRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // 加载当前封面图或生成默认封面
  useEffect(() => {
    const loadCurrentCover = async () => {
      // 如果有封面图，加载它
      if (currentCoverImage) {
        // 如果是 base64，直接使用
        if (currentCoverImage.startsWith('data:image/')) {
          setCoverImageUrl(currentCoverImage)
          setCoverFileId(currentCoverImage)
          return
        }

        // 如果是 fileId，从 IndexedDB 加载
        try {
          const win = window as any
          if (typeof win.loadMediaFromIndexedDB === 'function') {
            const mediaData = await win.loadMediaFromIndexedDB(currentCoverImage)
            if (mediaData && mediaData.url) {
              setCoverImageUrl(mediaData.url)
              setCoverFileId(currentCoverImage)
              return
            }
          }

          // 降级方案：直接使用 openDB
          if (typeof win.openDB === 'function') {
            const db = await win.openDB()
            const STORE_MEDIA = 'media'
            const mediaStore = db.transaction([STORE_MEDIA], 'readonly').objectStore(STORE_MEDIA)
            
            let originalFileId = currentCoverImage
            if (currentCoverImage.includes('.')) {
              originalFileId = currentCoverImage.substring(0, currentCoverImage.lastIndexOf('.'))
            }
            
            const result = await new Promise((resolve, reject) => {
              const request = mediaStore.get(originalFileId)
              request.onsuccess = () => resolve(request.result)
              request.onerror = () => reject(request.error)
            })
            
            if (result && (result as any).blob) {
              setCoverImageUrl(URL.createObjectURL((result as any).blob))
              setCoverFileId(currentCoverImage)
              return
            }
          }
        } catch (error) {
          console.error('加载当前封面图失败:', error)
        }
      }

      // 如果没有封面图，尝试生成默认封面
      if (!currentCoverImage) {
        try {
          const win = window as any
          // 获取当前项目
          if (typeof win.getCurrentProject === 'function') {
            const currentProject = win.getCurrentProject()
            if (currentProject && currentProject.items && currentProject.items.length > 0) {
              // 生成默认封面
              if (typeof win.generateProjectCover === 'function') {
                console.log('📸 生成默认封面图...')
                const coverResult = await win.generateProjectCover(currentProject)
                if (coverResult && coverResult.fileId) {
                  // 保存封面图 fileId
                  setCoverFileId(coverResult.fileId)
                  if (coverResult.thumbnailFileId) {
                    setCoverThumbnailFileId(coverResult.thumbnailFileId)
                  }
                  
                  // 加载封面图显示
                  if (coverResult.fileId.startsWith('data:image/')) {
                    setCoverImageUrl(coverResult.fileId)
                  } else {
                    // 从 IndexedDB 加载
                    if (typeof win.loadMediaFromIndexedDB === 'function') {
                      const thumbnailFileId = coverResult.thumbnailFileId || coverResult.fileId
                      const mediaData = await win.loadMediaFromIndexedDB(thumbnailFileId)
                      if (mediaData && mediaData.url) {
                        setCoverImageUrl(mediaData.url)
                      }
                    } else if (typeof win.openDB === 'function') {
                      const db = await win.openDB()
                      const STORE_MEDIA = 'media'
                      const mediaStore = db.transaction([STORE_MEDIA], 'readonly').objectStore(STORE_MEDIA)
                      
                      const thumbnailFileId = coverResult.thumbnailFileId || coverResult.fileId
                      let originalFileId = thumbnailFileId
                      if (thumbnailFileId.includes('.')) {
                        originalFileId = thumbnailFileId.substring(0, thumbnailFileId.lastIndexOf('.'))
                      }
                      
                      const result = await new Promise((resolve, reject) => {
                        const request = mediaStore.get(originalFileId)
                        request.onsuccess = () => resolve(request.result)
                        request.onerror = () => reject(request.error)
                      })
                      
                      if (result && (result as any).blob) {
                        setCoverImageUrl(URL.createObjectURL((result as any).blob))
                      }
                    }
                  }
                }
              }
            }
          }
        } catch (error) {
          console.error('生成默认封面图失败:', error)
        }
      }
    }

    loadCurrentCover()
  }, [currentCoverImage, projectId])

  // 处理图片裁剪（4:3比例，截取最大部分）
  const cropImageToAspectRatio = async (file: File, aspectRatio: number = 4 / 3): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      const url = URL.createObjectURL(file)
      
      img.onload = () => {
        URL.revokeObjectURL(url)
        
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('无法创建画布上下文'))
          return
        }

        const imgWidth = img.width
        const imgHeight = img.height
        const imgAspectRatio = imgWidth / imgHeight

        let cropWidth: number
        let cropHeight: number
        let cropX = 0
        let cropY = 0

        // 计算裁剪区域（保持4:3比例，截取最大部分）
        if (imgAspectRatio > aspectRatio) {
          // 图片更宽，以高度为准
          cropHeight = imgHeight
          cropWidth = imgHeight * aspectRatio
          cropX = (imgWidth - cropWidth) / 2
        } else {
          // 图片更高，以宽度为准
          cropWidth = imgWidth
          cropHeight = imgWidth / aspectRatio
          cropY = (imgHeight - cropHeight) / 2
        }

        // 设置画布尺寸（800x600，4:3比例）
        canvas.width = 800
        canvas.height = 600

        // 绘制裁剪后的图片
        ctx.drawImage(
          img,
          cropX, cropY, cropWidth, cropHeight,
          0, 0, canvas.width, canvas.height
        )

        // 转换为 Blob
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('图片转换失败'))
          }
        }, 'image/jpeg', 0.85)
      }

      img.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error('图片加载失败'))
      }

      img.src = url
    })
  }

  // 处理图片文件
  const processImageFile = async (file: File) => {
    try {
      setIsProcessing(true)
      setError(null)

      // 裁剪图片
      const croppedBlob = await cropImageToAspectRatio(file)
      
      // 保存原图到 IndexedDB
      const win = window as any
      if (typeof win.saveMediaToIndexedDB !== 'function') {
        throw new Error('保存图片功能不可用')
      }

      const coverFile = new File([croppedBlob], `cover_${projectId}.jpg`, {
        type: 'image/jpeg'
      })
      const fileId = await win.saveMediaToIndexedDB(coverFile)

      // 生成缩略图（400x300）
      const thumbnailCanvas = document.createElement('canvas')
      thumbnailCanvas.width = 400
      thumbnailCanvas.height = 300
      const thumbnailCtx = thumbnailCanvas.getContext('2d')
      
      if (!thumbnailCtx) {
        throw new Error('无法创建缩略图画布')
      }

      const thumbnailImg = new Image()
      thumbnailImg.src = URL.createObjectURL(croppedBlob)
      
      await new Promise((resolve, reject) => {
        thumbnailImg.onload = () => {
          thumbnailCtx.drawImage(thumbnailImg, 0, 0, 400, 300)
          URL.revokeObjectURL(thumbnailImg.src)
          resolve(null)
        }
        thumbnailImg.onerror = reject
      })

      thumbnailCanvas.toBlob(async (thumbnailBlob) => {
        if (!thumbnailBlob) {
          setCoverFileId(fileId)
          setCoverImageUrl(URL.createObjectURL(croppedBlob))
          setIsProcessing(false)
          return
        }

        try {
          const thumbnailFile = new File([thumbnailBlob], `cover_thumb_${projectId}.jpg`, {
            type: 'image/jpeg'
          })
          const thumbnailFileId = await win.saveMediaToIndexedDB(thumbnailFile)

          setCoverFileId(fileId)
          setCoverThumbnailFileId(thumbnailFileId)
          setCoverImageUrl(URL.createObjectURL(croppedBlob))
        } catch (error) {
          console.error('保存缩略图失败:', error)
          setCoverFileId(fileId)
          setCoverImageUrl(URL.createObjectURL(croppedBlob))
        } finally {
          setIsProcessing(false)
        }
      }, 'image/jpeg', 0.7)
    } catch (error: any) {
      console.error('处理图片失败:', error)
      setError('处理图片失败: ' + error.message)
      setIsProcessing(false)
    }
  }

  // 处理粘贴事件
  const handlePaste = useCallback(async (e: ClipboardEvent) => {
    const items = e.clipboardData?.items
    if (!items) return

    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault()
        const file = item.getAsFile()
        if (file) {
          await processImageFile(file)
        }
        break
      }
    }
  }, [])

  // 处理文件选择
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && file.type.startsWith('image/')) {
      await processImageFile(file)
    } else {
      setError('请选择图片文件')
    }
  }

  // 注册粘贴事件
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // 当焦点在封面区域时，监听 Ctrl+V
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        const activeElement = document.activeElement
        if (coverAreaRef.current?.contains(activeElement) || document.activeElement === document.body) {
          // 延迟一下，确保粘贴事件能触发
          setTimeout(() => {
            // 粘贴事件会在 handlePaste 中处理
          }, 0)
        }
      }
    }

    window.addEventListener('paste', handlePaste as any)
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('paste', handlePaste as any)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [handlePaste])

  // 根据画布生成封面
  const handleGenerateCoverFromCanvas = async () => {
    try {
      setIsGeneratingCover(true)
      setError(null)

      const win = window as any
      
      // 检查 generateProjectCover 函数是否可用
      if (typeof win.generateProjectCover !== 'function') {
        throw new Error('生成封面功能不可用，请刷新页面后重试')
      }

      // 获取当前项目
      if (typeof win.getCurrentProject !== 'function') {
        throw new Error('获取项目数据功能不可用')
      }

      const currentProject = win.getCurrentProject()
      if (!currentProject) {
        throw new Error('无法获取当前项目')
      }

      // 检查项目是否有内容
      if (!currentProject.items || currentProject.items.length === 0) {
        throw new Error('当前画布没有内容，无法生成封面')
      }

      // 生成封面
      const coverResult = await win.generateProjectCover(currentProject)
      
      if (coverResult && coverResult.fileId) {
        // 保存封面图 fileId
        setCoverFileId(coverResult.fileId)
        if (coverResult.thumbnailFileId) {
          setCoverThumbnailFileId(coverResult.thumbnailFileId)
        }
        
        // 加载封面图显示
        if (coverResult.fileId.startsWith('data:image/')) {
          setCoverImageUrl(coverResult.fileId)
        } else {
          // 从 IndexedDB 加载
          if (typeof win.loadMediaFromIndexedDB === 'function') {
            const thumbnailFileId = coverResult.thumbnailFileId || coverResult.fileId
            const mediaData = await win.loadMediaFromIndexedDB(thumbnailFileId)
            if (mediaData && mediaData.url) {
              setCoverImageUrl(mediaData.url)
            }
          } else if (typeof win.openDB === 'function') {
            const db = await win.openDB()
            const STORE_MEDIA = 'media'
            const mediaStore = db.transaction([STORE_MEDIA], 'readonly').objectStore(STORE_MEDIA)
            
            const thumbnailFileId = coverResult.thumbnailFileId || coverResult.fileId
            let originalFileId = thumbnailFileId
            if (thumbnailFileId.includes('.')) {
              originalFileId = thumbnailFileId.substring(0, thumbnailFileId.lastIndexOf('.'))
            }
            
            const result = await new Promise((resolve, reject) => {
              const request = mediaStore.get(originalFileId)
              request.onsuccess = () => resolve(request.result)
              request.onerror = () => reject(request.error)
            })
            
            if (result && (result as any).blob) {
              setCoverImageUrl(URL.createObjectURL((result as any).blob))
            }
          }
        }
      } else {
        throw new Error('封面生成失败：返回结果为空')
      }
    } catch (error: any) {
      console.error('生成封面失败:', error)
      setError('生成封面失败: ' + error.message)
    } finally {
      setIsGeneratingCover(false)
    }
  }

  // 处理确认
  const handleConfirm = async () => {
    if (!name.trim()) {
      setError('请输入画布名称')
      return
    }

    try {
      setIsProcessing(true)
      setError(null)
      await onConfirm({
        name: name.trim(),
        coverImage: coverFileId || currentCoverImage || null,
        coverThumbnailFileId: coverThumbnailFileId || null,
        description: description.trim()
      })
      onClose()
    } catch (error: any) {
      setError('保存失败: ' + error.message)
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* 头部 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-black text-gray-800">上传项目到云端</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            disabled={isProcessing}
          >
            <X size={24} className="text-gray-500" />
          </button>
        </div>

        {/* 内容 */}
        <div className="p-6 space-y-6">
          {/* 画布名称 */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              画布名称 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#ff5c5c] focus:ring-4 focus:ring-pink-100 outline-none transition-all"
              placeholder="请输入画布名称"
              disabled={isProcessing}
            />
          </div>

          {/* 封面图 */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-bold text-gray-700">
                封面图
              </label>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleGenerateCoverFromCanvas()
                }}
                disabled={isGeneratingCover || isProcessing}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="根据画布自动生成封面"
              >
                {isGeneratingCover ? (
                  <>
                    <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    <span>生成中...</span>
                  </>
                ) : (
                  <>
                    <RefreshCw size={14} />
                    <span>从画布生成</span>
                  </>
                )}
              </button>
            </div>
            <div
              ref={coverAreaRef}
              tabIndex={0}
              onClick={() => fileInputRef.current?.click()}
              className="relative w-full aspect-[4/3] border-2 border-dashed border-gray-300 rounded-xl overflow-hidden cursor-pointer hover:border-[#ff5c5c] transition-colors bg-gray-50 flex items-center justify-center group"
            >
              {coverImageUrl ? (
                <>
                  <img
                    src={coverImageUrl}
                    alt="封面图"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg text-sm font-bold text-gray-700">
                      点击更换 | Ctrl+V 粘贴
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center p-8">
                  <ImageIcon size={48} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium mb-1">点击选择封面图</p>
                  <p className="text-xs text-gray-400">或按 Ctrl+V 粘贴图片</p>
                  <p className="text-xs text-gray-400 mt-1">（将按 4:3 比例自动裁剪）</p>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                disabled={isProcessing || isGeneratingCover}
              />
            </div>
          </div>

          {/* 描述信息 */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              描述信息
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#ff5c5c] focus:ring-4 focus:ring-pink-100 outline-none transition-all resize-none"
              placeholder="请输入项目描述（可选）"
              disabled={isProcessing}
            />
          </div>

          {/* 错误提示 */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
              {error}
            </div>
          )}
        </div>

        {/* 底部按钮 */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            取消
          </button>
          <button
            onClick={handleConfirm}
            disabled={isProcessing || !name.trim()}
            className="px-6 py-3 bg-[#ff5c5c] hover:bg-[#ff4444] text-white rounded-xl font-bold shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isProcessing ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>处理中...</span>
              </>
            ) : (
              <>
                <Upload size={18} />
                <span>确定上传</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
